import type { HomeMarketingFeatureSlug } from "@/content/home-feature-modules";
import type { FeatureSlug } from "@/content/features";

/** Fixed shell height  -  both columns stretch to fill; tabs and preview share space evenly. */
export const HOME_FEATURE_SHOWCASE_GRID_HEIGHT_CLASS =
  "lg:h-[680px] lg:items-stretch";

/** Each sidebar tab gets an equal slice of the column (6 tabs). */
export const HOME_FEATURE_SHOWCASE_TAB_ROW_CLASS = "min-h-0 flex-1 basis-0";

/** Mobile uses explicit height; desktop grows to fill the column above the dot row. */
export const HOME_FEATURE_SHOWCASE_PREVIEW_VIEWPORT_CLASS =
  "h-[380px] shrink-0 overflow-hidden sm:h-[420px] lg:min-h-0 lg:flex-1";

/** Slides fill the viewport; chrome reset avoids double frames. */
export const homeFeatureShowcaseVisualScopeClass = [
  "[&_.hero-slide-shell]:!h-full [&_.hero-slide-shell]:!min-h-0",
  "[&_.hero-slide-shell]:!rounded-none [&_.hero-slide-shell]:!border-0 [&_.hero-slide-shell]:!shadow-none",
  "[&_.hero-slide-shell>div:first-child]:!rounded-none",
  "[&_[data-mockup-scene]]:!h-full [&_[data-mockup-scene]]:!min-h-0",
  "[&_[data-mockup-scene]]:!rounded-none [&_[data-mockup-scene]]:!border-0 [&_[data-mockup-scene]]:!shadow-none",
].join(" ");

export const HOME_FEATURE_SHOWCASE_ORDER: HomeMarketingFeatureSlug[] = [
  "write",
  "breakdown",
  "coverage",
  "schedule",
  "budget",
  "collaborate",
];

const HOME_FEATURE_SHOWCASE_ROUTE_SLUGS: Partial<
  Record<HomeMarketingFeatureSlug, FeatureSlug>
> = {
  write: "script",
  schedule: "schedule",
  budget: "budgeting",
};

export const getHomeFeatureShowcaseHref = (
  featureId: HomeMarketingFeatureSlug,
): string => {
  const routeSlug = HOME_FEATURE_SHOWCASE_ROUTE_SLUGS[featureId];
  if (routeSlug) {
    return `/features/${routeSlug}`;
  }
  return "/features";
};

export type FeatureShowcaseAccent = {
  bar: string;
  glowLight: string;
  glowDark: string;
  pillActive: string;
  previewBorder: string;
  previewShadow: string;
  mobileEyebrow: string;
};

export const FEATURE_SHOWCASE_ACCENTS: Record<
  HomeMarketingFeatureSlug,
  FeatureShowcaseAccent
> = {
  write: {
    bar: "bg-brand",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(255,197,57,0.14),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(255,197,57,0.12),transparent_70%)]",
    pillActive: "border-brand/30 bg-brand/10 text-amber-800 dark:text-brand",
    previewBorder: "border-brand/25 dark:border-brand/20",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(255,197,57,0.22)] dark:shadow-[0_32px_80px_-32px_rgba(255,197,57,0.28)]",
    mobileEyebrow: "text-amber-700 dark:text-brand",
  },
  breakdown: {
    bar: "bg-rose-400",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(251,113,133,0.16),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(251,113,133,0.14),transparent_70%)]",
    pillActive:
      "border-rose-400/30 bg-rose-400/10 text-rose-800 dark:text-rose-300",
    previewBorder: "border-rose-400/35 dark:border-rose-400/25",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(251,113,133,0.26)] dark:shadow-[0_32px_80px_-32px_rgba(251,113,133,0.32)]",
    mobileEyebrow: "text-rose-700 dark:text-rose-300",
  },
  coverage: {
    bar: "bg-sky-400",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(56,189,248,0.14),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(56,189,248,0.12),transparent_70%)]",
    pillActive:
      "border-sky-400/30 bg-sky-400/10 text-sky-800 dark:text-sky-300",
    previewBorder: "border-sky-400/30 dark:border-sky-400/22",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(56,189,248,0.24)] dark:shadow-[0_32px_80px_-32px_rgba(56,189,248,0.3)]",
    mobileEyebrow: "text-sky-700 dark:text-sky-300",
  },
  schedule: {
    bar: "bg-violet-400",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(167,139,250,0.14),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(167,139,250,0.12),transparent_70%)]",
    pillActive:
      "border-violet-400/30 bg-violet-400/10 text-violet-800 dark:text-violet-300",
    previewBorder: "border-violet-400/35 dark:border-violet-400/25",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(167,139,250,0.26)] dark:shadow-[0_32px_80px_-32px_rgba(167,139,250,0.32)]",
    mobileEyebrow: "text-violet-700 dark:text-violet-300",
  },
  budget: {
    bar: "bg-emerald-400",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(52,211,153,0.14),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(52,211,153,0.12),transparent_70%)]",
    pillActive:
      "border-emerald-400/30 bg-emerald-400/10 text-emerald-800 dark:text-emerald-300",
    previewBorder: "border-emerald-400/35 dark:border-emerald-400/25",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(52,211,153,0.26)] dark:shadow-[0_32px_80px_-32px_rgba(52,211,153,0.32)]",
    mobileEyebrow: "text-emerald-700 dark:text-emerald-300",
  },
  collaborate: {
    bar: "bg-zinc-400 dark:bg-zinc-300",
    glowLight:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(161,161,170,0.12),transparent_70%)]",
    glowDark:
      "bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(212,212,216,0.08),transparent_70%)]",
    pillActive:
      "border-zinc-400/30 bg-zinc-400/10 text-zinc-800 dark:text-zinc-200",
    previewBorder: "border-zinc-300/50 dark:border-zinc-400/20",
    previewShadow:
      "shadow-[0_24px_60px_-28px_rgba(161,161,170,0.18)] dark:shadow-[0_32px_80px_-32px_rgba(161,161,170,0.22)]",
    mobileEyebrow: "text-zinc-600 dark:text-zinc-300",
  },
};
